import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import Grid from '@mui/material/Grid';
import { Alert, Collapse } from '@mui/material';
import PopupSelector from '../PopupSelector';
import { relevant } from '../../api';
import { UserIdModule } from '../../api/relevant';
import BrowserUtils from '../../lib/browserUtils';

function UserIdModulesForm({
	availableModules,
	selectedModule,
	name,
	isAdd,
	onSelectModule,
	onNameChange,
	nameTextFieldRef,
}) {
	return (
		<Grid container spacing={4} direction="row">
			{selectedModule && (
				<>
					<Collapse in={selectedModule.type === 'GenericUserIdModule'} mountOnEnter unmountOnExit>
						<Alert
							severity="warning"
							elevation={0}
							style={{ marginTop: 32, marginLeft: 32 }}
						>
							This is a generic User ID module.
							It may not have all its available Prebid settings, nor work as intended, so use with caution.
						</Alert>
					</Collapse>
					<Grid item xs={6} mt={1}>
						<FormControl fullWidth>
							<InputLabel id="userid-select-label">Type</InputLabel>
							<Select
								labelId="userid-select-label"
								value={selectedModule.prebidName}
								onChange={onSelectModule}
								disabled={!isAdd}
								label="Type"
							>
								{availableModules.map(({ prebidName, friendlyTypeName: moduleTypeName }) => (
									<MenuItem
										key={prebidName}
										value={prebidName}
									>
										{moduleTypeName}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
					<Grid item xs={6} mt={1}>
						<TextField
							fullWidth
							label="Name"
							onChange={onNameChange}
							value={name}
							placeholder={selectedModule?.friendlyTypeName}
							inputRef={nameTextFieldRef}
						/>
					</Grid>
				</>
			)}
		</Grid>
	);
}

function UserIdModulesEditor({
	match, backUrl, desc, onChange,
}) {
	const history = useHistory();
	const [availableModules, setAvailableModules] = useState([]);
	const [selectedModule, setSelectedModule] = useState();
	const [name, setName] = useState('');
	const nameTextFieldRef = useRef();

	const id = match.params.id.toLowerCase();
	const isAdd = id === 'add';
	const goBack = () => backUrl && history.push(backUrl);

	const init = useCallback(async () => {
		if (!isAdd) {
			const [modules, currentModule] = await Promise.all([
				UserIdModule.call('getAvailableModules'),
				UserIdModule.get(id),
			]);
			setAvailableModules(modules);
			setName(currentModule.name);
			setSelectedModule(currentModule);
			return { modules, currentModule };
		}
		const modules = await UserIdModule.call('getAvailableModules');
		setAvailableModules(modules);
		const firstNonGenericModule = modules.find(({ type }) => type !== 'GenericUserIdModule');
		setSelectedModule(firstNonGenericModule);
		return { modules };
	}, [id, isAdd]);

	const save = async () => {
		if (isAdd) {
			const { publisherId } = BrowserUtils.urlParams();
			const {
				type,
				friendlyTypeName,
				prebidName,
				moduleName,
			} = availableModules.find((m) => m.prebidName === selectedModule.prebidName);

			await UserIdModule.add({
				name,
				type,
				friendlyTypeName,
				prebidName,
				moduleName,
				...(publisherId && { publisherId }),
			});
		} else {
			await relevant.put(`/${'useridmodules'}/${id}`, { name });
		}
		onChange();
	};

	const onSelectModule = ({ target }) => {
		const selected = availableModules.find(({ prebidName }) => prebidName === target.value);
		setSelectedModule(selected);
	};

	return (
		<PopupSelector
			title={`${isAdd ? 'Add' : 'Edit'} ${desc}`}
			fn={init}
			size="sm"
			forceExpanded
			allowErrorsIfCancel
			okLabel="SAVE"
			customLink={() => undefined}
			onApplyChanges={async () => {
				await save();
				goBack();
			}}
			onCancel={goBack}
			content={() => (
				<UserIdModulesForm
					availableModules={availableModules}
					selectedModule={selectedModule}
					name={name}
					isAdd={isAdd}
					onSelectModule={onSelectModule}
					onNameChange={({ target }) => setName(target.value)}
					nameTextFieldRef={nameTextFieldRef}
				/>
			)}
		/>
	);
}

export default UserIdModulesEditor;

UserIdModulesEditor.propTypes = {
	match: PropTypes.shape({
		params: PropTypes.shape({
			id: PropTypes.string.isRequired,
		}),
	}).isRequired,
	backUrl: PropTypes.string.isRequired,
	desc: PropTypes.string.isRequired,
	onChange: PropTypes.func,
};

UserIdModulesEditor.defaultProps = {
	onChange: () => undefined,
};
